import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  useInfiniteQuerySessionPackSubscriptionCurrentAsMember,
  useInfiniteQuerySessionPackSubscriptionPreviousAsMember,
  useInfiniteQuerySessionPackSubscriptionUsageHistoryAsMember,
} from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { RouteFeature } from "apps/portal/src/routes";
import useGymflowModels from "apps/portal/src/store";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useIsLargeScreen } from "../../../hooks/useIsLargeScreen";
import {
  Button,
  InfiniteScrollContainer,
  PlusIcon,
  SearchIcon,
  SelectInput,
  ShopBagIcon,
  Spinner,
  Tabs,
} from "../../atoms";
import { CreditPackItem } from "../../atoms/CreditPackItem/CreditPackItem";
import { EmptyPage } from "../../atoms/EmptyPage";
import { CursorClick02Icon } from "../../atoms/icons/CursorClick02Icon";
import { UsageItem } from "../../atoms/UsageItem/UsageItem";
import { HostedPagesBackHeader } from "../components/HostedPagesBackHeader";
import { HostedPagesProfileCard } from "../components/HostedPagesProfileCard";
import useRedirectIfNotAuthenticated from "../useRedirectIfNotAuthenticated";
import { PACKAGES_TABS, PACKAGES_TABS_SELECT } from "./constants";
import { PackagesTabsType } from "./types";

export const HostedPagesProfilePackages = () => {
  const { createSiteOrEmbedLink } = usePortalRoutes();

  useRedirectIfNotAuthenticated(
    createSiteOrEmbedLink(
      RouteFeature.SiteBuyMembershipType.replace(":membershipType", "all"),
    ),
  );

  const [selectedItemId, setSelectedItemId] = useState<number | undefined>();
  const [activeTabId, _setActiveTabId] = useState<PackagesTabsType>(
    PACKAGES_TABS[0].id,
  );

  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const [parent] = useAutoAnimate();
  const isLargeScreen = useIsLargeScreen();

  const queryUserMemberSessionPackSubscriptionCurrent =
    useInfiniteQuerySessionPackSubscriptionCurrentAsMember({
      api,
      enabled: activeTabId === "CURRENT",
    });
  const queryUserMemberSessionPackSubscriptionPrevious =
    useInfiniteQuerySessionPackSubscriptionPreviousAsMember({
      api,
      enabled: activeTabId === "PREVIOUS",
    });

  const queryCreditPacks =
    activeTabId === "CURRENT"
      ? queryUserMemberSessionPackSubscriptionCurrent
      : queryUserMemberSessionPackSubscriptionPrevious;
  const { data: dataCreditPacks, isLoading: isLoadingCreditPacks } =
    queryCreditPacks;
  const listOfCreditPacks =
    dataCreditPacks?.pages.flatMap((e) => e.content) || [];

  const queryUsageHistory =
    useInfiniteQuerySessionPackSubscriptionUsageHistoryAsMember({
      api,
      subscriptionId: selectedItemId || 0,
      enabled: !!selectedItemId,
    });

  const { data: dataUsageHistory, isLoading: isLoadingUsageHistory } =
    queryUsageHistory;
  const listOfUsage = dataUsageHistory?.pages.flatMap((e) => e.content) || [];

  const info = useMemo(() => {
    if (!listOfCreditPacks?.length || (selectedItemId && !listOfUsage.length)) {
      return {
        icon: (
          <SearchIcon
            className="size-6"
            pathClassName="stroke-gray-500 dark:stroke-gray-400"
          />
        ),
        title: t(
          "page.hostedPagesProfilePackages.usageList.info.noUsageYet.title",
        ),
        description: null,
      };
    }

    if (!selectedItemId) {
      return {
        icon: (
          <CursorClick02Icon
            className="size-6"
            pathClassName="stroke-gray-500 dark:stroke-gray-400"
          />
        ),
        title: t(
          "page.hostedPagesProfilePackages.usageList.info.selectPackage.title",
        ),
        description: t(
          "page.hostedPagesProfilePackages.usageList.info.selectPackage.description",
        ),
      };
    }
    return { icon: null, title: null, description: null };
  }, [listOfCreditPacks?.length, selectedItemId, listOfUsage.length, t]);

  const setActiveTabId = (id: PackagesTabsType) => {
    _setActiveTabId(id);
    setSelectedItemId(undefined);
  };

  const isContentReady =
    listOfCreditPacks?.length >= 0 && !isLoadingCreditPacks;

  return (
    <div className="track-height flex size-full flex-col">
      <HostedPagesBackHeader
        label={t("page.hostedPagesProfilePackages.header")}
        rightSideOptions={
          <div className="flex flex-row items-center justify-center gap-2">
            <div className="block lg:hidden">
              <SelectInput
                className="w-[7.5rem]"
                noTruncateSelectedLabel
                options={PACKAGES_TABS_SELECT}
                value={PACKAGES_TABS_SELECT.find(
                  ({ id }) => id === activeTabId,
                )}
                onChange={(selected) => {
                  if (!selected) return;
                  setActiveTabId(selected.value);
                }}
              />
            </div>
            <Button
              intent="secondary-outline"
              className="h-fit gap-1 p-2.5 lg:px-3"
              size="medium"
              link={createSiteOrEmbedLink(
                RouteFeature.SiteBuySessionPackType.replace(
                  ":sessionPackType",
                  "all",
                ),
              )}
            >
              <PlusIcon
                className="!size-5"
                pathClassName="stroke-secondary-500"
              />
              <div className="hidden lg:block">
                {t("page.hostedPagesProfilePackages.buyPackage")}
              </div>
            </Button>
          </div>
        }
      />
      <div
        ref={parent}
        className="track-height flex h-full max-h-full flex-col items-center justify-center gap-0 bg-gray-50 p-0 pb-4 lg:flex-row lg:gap-4 lg:p-6 lg:px-28 dark:bg-gray-800"
      >
        <HostedPagesProfileCard.Container
          className={cn(
            "relative w-full h-full max-h-full flex lg:max-h-full lg:flex-1 flex-col overflow-hidden p-0 !bg-transparent lg:!bg-gray-0 !shadow-transparent lg:!shadow-xs !border-transparent lg:!border lg:!border-gray-200 lg:dark:!border-gray-800 lg:dark:!bg-gray-950",
            {
              "min-h-fit max-h-fit": isContentReady,
            },
          )}
        >
          <div
            className={cn(
              "absolute inset-0 flex flex-col overflow-y-auto overflow-x-hidden",
              {
                "relative lg:absolute": isContentReady,
              },
            )}
          >
            <Tabs
              tabs={PACKAGES_TABS}
              activeTabId={activeTabId}
              setActiveTabId={setActiveTabId}
              className="hidden justify-evenly p-4 lg:flex"
              tabClassName="py-2 px-3 max-w-none w-full items-center justify-center border border-gray-200 rounded-lg dark:border-gray-600"
              activeTabClassName="border-secondary-500 dark:border-secondary-500"
            />
            <InfiniteScrollContainer
              className="flex flex-1 flex-row gap-3 overflow-x-auto p-4 lg:!flex-col lg:overflow-y-auto"
              direction={isLargeScreen ? "vertical" : "horizontal"}
              query={queryCreditPacks}
            >
              {listOfCreditPacks?.length > 0 ? (
                listOfCreditPacks.map((item) => (
                  <CreditPackItem
                    key={item.id}
                    creditPack={item}
                    as="member"
                    activeTabId={activeTabId}
                    setSelectedItemId={setSelectedItemId}
                    selectedItemId={selectedItemId}
                  />
                ))
              ) : isLoadingCreditPacks ? (
                <Spinner className="size-7" />
              ) : (
                <div className="flex w-full flex-col items-center justify-center lg:pt-10">
                  <EmptyPage
                    textOverride={t(
                      "page.hostedPagesProfilePackages.emptyPage.title",
                    )}
                    description={t(
                      "page.hostedPagesProfilePackages.emptyPage.description",
                    )}
                    action={
                      <Button
                        intent="secondary"
                        className="gap-1.5"
                        link={createSiteOrEmbedLink(
                          RouteFeature.SiteBuySessionPackType.replace(
                            ":sessionPackType",
                            "all",
                          ),
                        )}
                      >
                        <ShopBagIcon pathClassName="stroke-gray-0" />
                        {t("page.hostedPagesProfilePackages.emptyPage.action")}
                      </Button>
                    }
                  />
                </div>
              )}
            </InfiniteScrollContainer>
          </div>
        </HostedPagesProfileCard.Container>
        <div className="flex size-full max-h-full flex-col overflow-hidden px-4 lg:flex-1 lg:p-0">
          <HostedPagesProfileCard.Container className="relative flex size-full max-h-full flex-col overflow-hidden p-0 lg:flex-1">
            <div className="absolute inset-0 flex flex-col gap-4 overflow-y-auto overflow-x-hidden">
              <div className="dark:text-gray-25 ml-4 mt-4 text-lg font-semibold text-gray-950">
                {t("page.hostedPagesProfilePackages.usageList.title")}
              </div>
              {selectedItemId && listOfUsage?.length > 0 ? (
                <InfiniteScrollContainer
                  className="flex flex-1 flex-col overflow-y-auto px-4 pb-4"
                  direction="vertical"
                  query={queryUsageHistory}
                >
                  {listOfUsage.map((item, index) => (
                    <UsageItem key={`${item.name}-${index}`} usageInfo={item} />
                  ))}
                </InfiniteScrollContainer>
              ) : isLoadingUsageHistory ? (
                <Spinner className="size-7" />
              ) : (
                <div className="flex flex-col items-center justify-center gap-4 overflow-y-auto pt-10">
                  <div className="flex size-14 shrink-0 items-center justify-center rounded-full border-8 border-gray-100 bg-gray-200 dark:border-gray-900 dark:bg-gray-600">
                    {info.icon}
                  </div>
                  <div className="flex flex-col items-center justify-center gap-1">
                    <div className="dark:text-gray-25 text-base font-semibold text-gray-950">
                      {info.title}
                    </div>
                    <div className="px-10 text-center text-sm font-normal text-gray-500 dark:text-gray-400">
                      {info.description}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </HostedPagesProfileCard.Container>
        </div>
      </div>
    </div>
  );
};
